import { useEffect, useRef, useState, type FocusEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SenseDetails } from '@features/CardsWorkspace';
import { cn } from '@shared/lib';
import type { TopicPair } from './selectionModel';

/** Мышь: подробности через паузу, чтобы не мигали при проходе курсором. */
const HOVER_MS = 280;
/** Палец: долгое нажатие, как на обычном выборе значений (ChooseTranslations). */
const HOLD_MS = 420;

export type ChipTone = 'on' | 'off' | 'pending' | 'done' | 'generating' | 'unavailable';

export type PeekState = { id: string; mode: 'hover' | 'hold' } | null;

/** Есть ли что показать в подробностях: синонимы, помета или пример. */
export const hasDetails = (pair: TopicPair) =>
  !!pair.details && (pair.details.translations.length > 1 || !!pair.details.usageNote || !!pair.details.example);

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export function PairDetails({ pair }: { pair: TopicPair }) {
  if (!pair.details) return null;
  return <SenseDetails x={{ ...pair.details, card: pair.word }} />;
}

export function TopicChip({
  pair,
  tone,
  locked,
  showPos,
  tag,
  note,
  failed,
  peek,
  onToggle,
  onPeek,
}: {
  pair: TopicPair;
  tone: ChipTone;
  /** Нельзя переключить: нет модуля, идёт создание, значение недоступно или уже есть. */
  locked: boolean;
  showPos: boolean;
  tag: string | null;
  /** Почему значение нельзя выбрать. Не системный title: тот накладывался на карточку подробностей (ревью 2026-10-19). */
  note?: string;
  failed: boolean;
  peek: PeekState;
  onToggle: () => void;
  onPeek: (next: PeekState) => void;
}) {
  const { t } = useTranslation();
  const wrap = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const holdFired = useRef(false);
  const [place, setPlace] = useState({ shift: 0, above: false });
  const info = hasDetails(pair);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const showHover = () => {
    // Карточка сдвигается влево ровно настолько, чтобы не выйти за правый край списка слов,
    // и не дальше левого края экрана (раньше прижималась правым краем к чипу и на узком
    // экране уезжала влево за край — 2026-10-16).
    const box = wrap.current?.getBoundingClientRect();
    const edge = Math.min(
      wrap.current?.closest('.tc-words')?.getBoundingClientRect().right ?? Infinity,
      window.innerWidth - 16,
    );
    const width = Math.min(320, window.innerWidth - 32);
    const left = box ? Math.max(16, Math.min(box.left, edge - width)) : 0;
    // Внизу экрана — над чипом: под ним карточку закрыла бы нижняя панель.
    setPlace({ shift: box ? left - box.left : 0, above: !!box && box.bottom + 300 > window.innerHeight - 110 });
    onPeek({ id: pair.id, mode: 'hover' });
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    if (peek?.id === pair.id && peek.mode === 'hover') onPeek(null);
  };

  const startHold = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      holdFired.current = true;
      if (navigator.vibrate) navigator.vibrate(8);
      onPeek({ id: pair.id, mode: 'hold' });
      // Закрывается, когда палец отпущен. Пока открыто, прокрутку и системное меню долгого
      // нажатия гасим: иначе Android забирает жест и подробности сразу пропадают.
      const noScroll = (event: TouchEvent) => event.preventDefault();
      const noMenu = (event: Event) => event.preventDefault();
      const off = () => {
        ['pointerup', 'touchend'].forEach((name) => window.removeEventListener(name, off, true));
        window.removeEventListener('touchmove', noScroll, true);
        window.removeEventListener('contextmenu', noMenu, true);
        onPeek(null);
      };
      ['pointerup', 'touchend'].forEach((name) => window.addEventListener(name, off, true));
      window.addEventListener('touchmove', noScroll, { capture: true, passive: false });
      window.addEventListener('contextmenu', noMenu, true);
    }, HOLD_MS);
  };

  const onFocus = (event: FocusEvent<HTMLButtonElement>) => {
    if (info && event.currentTarget.matches(':focus-visible')) showHover();
  };

  return (
    <span
      ref={wrap}
      className="tc-chip-wrap"
      // Только мышь: после касания браузер шлёт эмулированные mouseenter, и подсказка
      // открывалась от обычного тапа. Пальцем — только долгое нажатие (HoldPeek).
      onPointerEnter={
        info
          ? (event) => {
              if (event.pointerType !== 'mouse') return;
              window.clearTimeout(timer.current);
              timer.current = window.setTimeout(showHover, HOVER_MS);
            }
          : undefined
      }
      onPointerLeave={info ? (event) => event.pointerType === 'mouse' && hide() : undefined}
    >
      <button
        type="button"
        aria-pressed={locked ? undefined : tone === 'on'}
        aria-disabled={locked || undefined}
        aria-label={note ? `${pair.translation} — ${note}` : undefined}
        onClick={() => {
          if (holdFired.current) {
            holdFired.current = false;
            return;
          }
          if (!locked) onToggle();
        }}
        onPointerDown={(event) => {
          // Сброс на любом нажатии: если после долгого нажатия браузер не прислал click,
          // следующий обычный клик (в том числе мышью) не должен потеряться.
          holdFired.current = false;
          if (info && event.pointerType !== 'mouse') startHold();
        }}
        onPointerUp={() => window.clearTimeout(timer.current)}
        onPointerCancel={() => window.clearTimeout(timer.current)}
        onContextMenu={(event) => event.preventDefault()}
        onFocus={onFocus}
        onBlur={hide}
        className={cn('tc-chip', `is-${tone}`, failed && 'is-failed', locked && 'is-locked')}
      >
        {(tone === 'on' || tone === 'pending' || tone === 'done') && <CheckIcon />}
        {tone === 'generating' && <span className="tc-chip-dot" aria-hidden />}
        <span className="tc-chip-text">{pair.translation}</span>
        {showPos && (
          <span className="tc-chip-pos">
            {t(`cardsWorkspace.lookup.posShort.${pair.pos ?? 'other'}`, { defaultValue: pair.pos ?? '' })}
          </span>
        )}
        {tag && <span className="tc-chip-tag">{tag}</span>}
      </button>
      {peek?.id === pair.id && peek.mode === 'hover' && (
        <div className={cn('tc-peek', place.above && 'tc-peek--above')} style={{ left: place.shift }} role="tooltip">
          <PairDetails pair={pair} />
        </div>
      )}
    </span>
  );
}

/** Подробности значения на телефоне: по центру экрана, пока палец держит чип. */
export function HoldPeek({ pair, onClose }: { pair: TopicPair; onClose: () => void }) {
  const { t } = useTranslation();
  return createPortal(
    <div className="tc-hold" onClick={onClose} onContextMenu={(event) => event.preventDefault()}>
      <div className="tc-hold-card" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="tc-hold-close" onClick={onClose} aria-label={t('topicCards.peekClose')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        <div className="tc-hold-body">
          <PairDetails pair={pair} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
